import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeader from "@/components/base/SectionHeader";
import { moodMeters, pollOptions, pollQuestion } from "@/mocks/melons";

export default function MoodSection() {
  const [chosen, setChosen] = useState<string | null>(null);

  return (
    <section id="mood" className="bg-background-100/60">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          eyebrow="群众态度"
          eyebrowIcon="ri-emotion-line"
          title="围观群众的真实反应"
          desc="投票与态度指数只反映当前围观群众的分布，用来了解大家怎么聊、怎么想。点选一个选项即可实时查看占比。"
        />

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
          <Reveal>
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-soft md:p-6">
              <div className="flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-base font-semibold text-foreground-900">
                  <i className="ri-bar-chart-grouped-line text-primary-600" /> {pollQuestion}
                </p>
                {chosen && (
                  <button
                    type="button"
                    onClick={() => setChosen(null)}
                    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-background-200 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
                  >
                    <i className="ri-refresh-line" /> 重新投票
                  </button>
                )}
              </div>

              <div className="mt-5 flex flex-col gap-3">
                {pollOptions.map((opt) => {
                  const isChosen = chosen === opt.id;
                  const showResult = chosen !== null;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={showResult}
                      onClick={() => setChosen(opt.id)}
                      className={`relative overflow-hidden rounded-xl border px-4 py-3.5 text-left text-sm transition-colors ${
                        showResult
                          ? isChosen
                            ? "border-primary-500 bg-primary-50"
                            : "border-background-200 bg-background-100/60"
                          : "border-background-300 bg-background-100 hover:border-primary-400 hover:bg-background-50"
                      }`}
                    >
                      {showResult && (
                        <span
                          className={`absolute inset-y-0 left-0 ${isChosen ? "bg-primary-200/80" : "bg-primary-100/50"}`}
                          style={{ width: `${opt.ratio}%` }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-between gap-3">
                        <span className={`font-medium ${isChosen ? "text-primary-900" : "text-foreground-900"}`}>{opt.label}</span>
                        {showResult && <span className={`font-heading text-lg ${isChosen ? "text-primary-700" : "text-foreground-700"}`}>{opt.ratio}%</span>}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex items-center justify-between text-xs text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-heart-line text-accent-600" /> 本期参与围观投票：23,650 人
                </span>
                {!chosen && <span className="hidden sm:inline">点击选项即可查看占比</span>}
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-soft">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-pulse-line text-secondary-600" /> 群众反应 · 实时指数
                </p>
                <div className="mt-4 flex flex-col gap-3.5">
                  {moodMeters.map((m) => (
                    <div key={m.label} className="text-sm">
                      <div className="flex items-center justify-between text-foreground-800">
                        <span>{m.label}</span>
                        <span className="font-heading text-base text-foreground-900">{m.value}%</span>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-secondary-400 to-secondary-500"
                          style={{ width: `${m.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-primary-600 p-5 text-background-50">
                <p className="font-heading text-xl leading-tight">占多数的你，想聊两句吗？</p>
                <p className="mt-1.5 text-sm text-primary-50/90">你的态度指数会实时并入本期群众反应报告，每期月初公布全景数据。</p>
                <a href="#talk" className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-primary-700 transition-transform hover:-translate-y-0.5">
                  去表达观点 <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}